import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import {
  Menu, X, Phone, MapPin, Instagram,
  Thermometer, Clock, Truck, Package, Users,
  Sparkles, ChevronRight, Star, MessageCircle,
  Send, Calendar, User, Mail, FileText, CheckCircle, Loader2 } from
"lucide-react";

// Asset URLs
const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_d0860b6e-ff7c-40e0-97b6-94fa78ed3e09/artifacts/yxgiobyu_LOGO%20SEM%20FUNDO%20-%20BRANCA%20E%20AZUL.png",
  logoHeader: "https://customer-assets.emergentagent.com/job_multi-chopp-express/artifacts/kvuoq4d9_LOGO%20MULTICHOPP%20cinza%20escuro%20com%20azul.png",
  heroBackground: "https://customer-assets.emergentagent.com/job_multi-chopp-express/artifacts/vzs2u2h2_imagem%20da%20hero.png",
  chopeira: "https://customer-assets.emergentagent.com/job_multi-chopp-express/artifacts/x8mmfp2y_CHOPEIRA.png",
  kegs: {
    harden: {
      name: "Harden Chopp Pilsen",
      description: "Leve, refrescante e perfeito para qualquer ocasião",
      url: "https://customer-assets.emergentagent.com/job_multi-chopp-express/artifacts/p7nuw36i_harden.png"
    },
    brahma: {
      name: "Brahma Chopp",
      description: "Clássico, equilibrado e muito apreciado",
      url: "https://customer-assets.emergentagent.com/job_multi-chopp-express/artifacts/0amef8w5_brahma.png"
    },
    heineken: {
      name: "Heineken Chopp",
      description: "Mais encorpado, sabor marcante e premium",
      url: "https://customer-assets.emergentagent.com/job_multi-chopp-express/artifacts/8jucj7tx_heineken.png"
    },
    grapeCool: {
      name: "Chopp de Vinho Grape Cool",
      description: "Uma opção diferenciada e muito procurada",
      url: "https://customer-assets.emergentagent.com/job_multi-chopp-express/artifacts/kg3tcrch_barril%20grape%20cool-%20chopp%20de%20vinho.png"
    },
    sweetGrape: {
      name: "Chopp de Vinho Sweet Grape",
      description: "Sabor adocicado e refrescante, perfeito para paladares exigentes",
      url: "https://customer-assets.emergentagent.com/job_multi-chopp-express/artifacts/g8o6yeew_barril%20sweet%20grape%20-%20chopp%20de%20vinho.png"
    }
  },
  lifestyle: {
    festas: "https://customer-assets.emergentagent.com/job_multi-chopp-express/artifacts/zel6pip3_festas%20e%20confraterniza%C3%A7%C3%B5es.png",
    casamentos: "https://images.pexels.com/photos/3082764/pexels-photo-3082764.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    churrascos: "https://customer-assets.emergentagent.com/job_multi-chopp-express/artifacts/a2dy59f6_churrasco.png"
  }
};

const WHATSAPP_LINK = "https://wa.me/5541997019715?text=Olá,%20gostaria%20de%20pedir%20um%20chopp!";
const INSTAGRAM_LINK = "https://www.instagram.com/multichoppexpress/";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`
        }
        data-testid="header">

        <div className="container-main">
          <div className="flex items-center justify-between h-20">
            <a href="/" className="flex items-center" data-testid="logo-link">
              <img
                src={isScrolled ? ASSETS.logoHeader : ASSETS.logo}
                alt="Multi Chopp Express"
                className="h-10 md:h-12 w-auto" />

            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('marcas')} className={`${isScrolled ? 'text-[#1D1D1F]' : 'text-white'} hover:text-[#005BEA] transition-colors font-medium`}>
                Marcas
              </button>
              <button onClick={() => scrollToSection('como-funciona')} className={`${isScrolled ? 'text-[#1D1D1F]' : 'text-white'} hover:text-[#005BEA] transition-colors font-medium`}>
                Como Funciona
              </button>
              <button onClick={() => scrollToSection('eventos')} className={`${isScrolled ? 'text-[#1D1D1F]' : 'text-white'} hover:text-[#005BEA] transition-colors font-medium`}>
                Eventos
              </button>
              <button onClick={() => scrollToSection('orcamento')} className={`${isScrolled ? 'text-[#1D1D1F]' : 'text-white'} hover:text-[#005BEA] transition-colors font-medium`}>
                Orçamento
              </button>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-sm px-6 py-3"
                data-testid="header-cta-btn">

                Pedir Chopp
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setIsMobileMenuOpen(true)}
              data-testid="mobile-menu-btn">

              <Menu className={`w-6 h-6 ${isScrolled ? 'text-[#1D1D1F]' : 'text-white'}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen &&
        <motion.div
          className="mobile-menu"
          initial={{ opacity: 0, x: '100%' }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: '100%' }}
          transition={{ duration: 0.3 }}
          data-testid="mobile-menu">

            <div className="flex justify-between items-center mb-8">
              <img src={ASSETS.logo} alt="Multi Chopp Express" className="h-10" />
              <button onClick={() => setIsMobileMenuOpen(false)} data-testid="close-mobile-menu">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-6">
              <button onClick={() => scrollToSection('marcas')} className="text-xl font-medium text-left">
                Marcas
              </button>
              <button onClick={() => scrollToSection('como-funciona')} className="text-xl font-medium text-left">
                Como Funciona
              </button>
              <button onClick={() => scrollToSection('eventos')} className="text-xl font-medium text-left">
                Eventos
              </button>
              <button onClick={() => scrollToSection('orcamento')} className="text-xl font-medium text-left">
                Orçamento
              </button>
              <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp justify-center mt-4"
              data-testid="mobile-whatsapp-btn">

                <MessageCircle className="w-5 h-5" />
                Pedir no WhatsApp
              </a>
            </nav>
          </motion.div>
        }
      </AnimatePresence>
    </>);

};

// Hero Section
const HeroSection = () => {
  return (
    <section className="min-h-screen flex items-center pt-20 relative overflow-hidden" data-testid="hero-section">
      {/* Full-width Background Image */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${ASSETS.heroBackground}')`
          }} />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/60" />
      </div>

      <div className="container-main w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center py-12 lg:py-0">
          {/* Left Content */}
          <motion.div
            className="order-2 lg:order-1"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}>

            <h1 className="text-heading-1 text-white mb-6">
              O chopp perfeito para sua festa, entregue pronto para servir.
            </h1>
            <p className="max-w-xl mb-8 !text-[#FFFFFF]">
              Mais praticidade, mais sabor e zero preocupação. A Multi Chopp Express leva chopeira instalada e chopp gelado direto para o seu evento.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp justify-center"
                data-testid="hero-whatsapp-btn">

                <MessageCircle className="w-5 h-5" />
                Pedir Agora
              </a>
              <button
                onClick={() => document.getElementById('marcas').scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-medium transition-all duration-300"
                data-testid="hero-marcas-btn">

                Ver Marcas Disponíveis
              </button>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <MapPin className="w-4 h-4 text-[#005BEA]" />
              <span className="text-sm">Atendemos Curitiba e região metropolitana com agilidade e estrutura completa.</span>
            </div>
          </motion.div>

          {/* Right Content - Chopeira Image */}
          <motion.div
            className="order-1 lg:order-2 flex justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}>

            <motion.img
              src={ASSETS.chopeira}
              alt="Chopeira Multi Chopp Express"
              className="w-full max-w-md lg:max-w-lg drop-shadow-2xl"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} />

          </motion.div>
        </div>
      </div>
    </section>);

};

// Diferencial Section
const DiferencialSection = () => {
  const benefits = [
  { icon: <Thermometer className="w-6 h-6" />, title: "Chopeira elétrica instalada", description: "Equipamento profissional pronto para servir" },
  { icon: <Sparkles className="w-6 h-6" />, title: "Chopp sempre gelado", description: "Temperatura ideal do primeiro ao último copo" },
  { icon: <Truck className="w-6 h-6" />, title: "Entrega rápida e agendada", description: "Você escolhe o melhor horário" },
  { icon: <Package className="w-6 h-6" />, title: "Barris de 30L e 50L", description: "Tamanhos para todos os eventos" },
  { icon: <Users className="w-6 h-6" />, title: "Ideal para qualquer evento", description: "De churrascos a casamentos" }];


  return (
    <section className="section-padding bg-[#F5F5F7]" data-testid="diferencial-section">
      <div className="container-main">
        <motion.div
          className="text-center mb-16"
          {...fadeInUp}>

          <h2 className="text-heading-2 text-[#1D1D1F] mb-4">
            Você cuida da festa. A gente cuida do chopp.
          </h2>
          <p className="text-body text-[#86868B] max-w-2xl mx-auto">
            Com a Multi Chopp Express você não precisa se preocupar com nada.
            Levamos a chopeira, instalamos no local e deixamos tudo pronto para servir.
            Você só precisa chamar a galera e aproveitar.
          </p>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}>

          {benefits.map((benefit, index) =>
          <motion.div
            key={index}
            className="card-apple p-8"
            variants={fadeInUp}
            data-testid={`benefit-card-${index}`}>

              <div className="benefit-icon mb-4">
                {benefit.icon}
              </div>
              <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">{benefit.title}</h3>
              <p className="text-[#86868B]">{benefit.description}</p>
            </motion.div>
          )}
        </motion.div>

        <motion.div
          className="text-center mt-12"
          {...fadeInUp}>

          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="diferencial-cta-btn">

            Quero reservar meu chopp
            <ChevronRight className="w-5 h-5" />
          </a>
        </motion.div>
      </div>
    </section>);

};

// Marcas Section with Horizontal Scroll
const MarcasSection = () => {
  const kegs = [
    ASSETS.kegs.harden,
    ASSETS.kegs.brahma,
    ASSETS.kegs.heineken,
    ASSETS.kegs.grapeCool,
    ASSETS.kegs.sweetGrape
  ];

  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef(null);
  const itemWidth = 280; // width + margin

  // Duplicate array for infinite scroll effect
  const duplicatedKegs = [...kegs, ...kegs, ...kegs];

  const handleInteraction = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    // Resume after a delay when mouse leaves
    setTimeout(() => setIsPaused(false), 3000);
  };

  const scrollTo = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'next' ? itemWidth : -itemWidth;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section id="marcas" className="section-padding brand-section-dark" data-testid="marcas-section">
      <div className="container-main mb-12">
        <motion.div
          className="text-center"
          {...fadeInUp}>
          <h2 className="text-heading-2 text-white mb-4">
            Escolha o chopp ideal para o seu evento
          </h2>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            Trabalhamos com marcas reconhecidas e opções para todos os gostos
          </p>
        </motion.div>
      </div>

      {/* Horizontal Scroll Carousel with Controls */}
      <div 
        className="relative group/carousel"
        onMouseEnter={handleInteraction}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleInteraction}
      >
        {/* Navigation Buttons */}
        <AnimatePresence>
          {isPaused && (
            <>
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onClick={() => scrollTo('prev')}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-[#1D1D1F] p-3 md:p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
                data-testid="carousel-prev-btn"
              >
                <ChevronRight className="w-6 h-6 rotate-180" />
              </motion.button>
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onClick={() => scrollTo('next')}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-[#1D1D1F] p-3 md:p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
                data-testid="carousel-next-btn"
              >
                <ChevronRight className="w-6 h-6" />
              </motion.button>
            </>
          )}
        </AnimatePresence>

        {/* Paused indicator */}
        <AnimatePresence>
          {isPaused && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-2 left-1/2 -translate-x-1/2 z-20 bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full"
            >
              Deslize ou use as setas para navegar
            </motion.div>
          )}
        </AnimatePresence>

        <div 
          ref={scrollRef}
          className={`overflow-x-auto hide-scrollbar py-8 ${isPaused ? 'cursor-grab active:cursor-grabbing' : ''}`}
          style={{ scrollBehavior: 'smooth' }}
        >
          <motion.div
            className={`scroll-container ${isPaused ? '' : 'animate-marquee'}`}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {duplicatedKegs.map((keg, index) =>
              <div
                key={index}
                className="flex-shrink-0 w-64 mx-6 text-center group"
                data-testid={`keg-card-${index % kegs.length}`}
              >
                <div className="p-4 mb-4 transition-transform duration-300 group-hover:scale-105">
                  <img
                    src={keg.url}
                    alt={keg.name}
                    className="w-full h-56 object-contain keg-image drop-shadow-2xl"
                  />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{keg.name}</h3>
                <p className="text-zinc-400 text-sm">{keg.description}</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <motion.div
        className="container-main text-center mt-8"
        {...fadeInUp}>
        <p className="text-zinc-500 mb-6">Outras opções disponíveis - Consulte nossa equipe</p>
        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-whatsapp inline-flex"
          data-testid="marcas-whatsapp-btn"
        >
          <MessageCircle className="w-5 h-5" />
          Falar no WhatsApp
        </a>
      </motion.div>
    </section>
  );
};

// Como Funciona Section
const ComoFuncionaSection = () => {
  const steps = [
  { number: "01", title: "Você escolhe o chopp", description: "Definimos a melhor opção para seu evento" },
  { number: "02", title: "Agendamos a entrega", description: "Com data e horário combinados" },
  { number: "03", title: "Instalamos no local", description: "Tudo pronto para servir" },
  { number: "04", title: "Você aproveita", description: "Sem dor de cabeça e com chopp gelado" }];


  return (
    <section id="como-funciona" className="section-padding bg-white" data-testid="como-funciona-section">
      <div className="container-main">
        <motion.div
          className="text-center mb-16"
          {...fadeInUp}>

          <h2 className="text-heading-2 text-[#1D1D1F] mb-4">
            Simples, rápido e sem complicação
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) =>
          <motion.div
            key={index}
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            data-testid={`step-${index}`}>

              <div className="step-number mb-4">{step.number}</div>
              <h3 className="text-xl font-semibold text-[#1D1D1F] mb-2">{step.title}</h3>
              <p className="text-[#86868B]">{step.description}</p>
            </motion.div>
          )}
        </div>

        <motion.div
          className="text-center mt-12"
          {...fadeInUp}>

          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="como-funciona-cta-btn">

            Agendar agora
            <ChevronRight className="w-5 h-5" />
          </a>
        </motion.div>
      </div>
    </section>);

};

// Ideal Para Section
const IdealParaSection = () => {
  const events = [
  { title: "Festas e Confraternizações", image: ASSETS.lifestyle.festas },
  { title: "Casamentos", image: ASSETS.lifestyle.casamentos },
  { title: "Churrascos e Eventos", image: ASSETS.lifestyle.churrascos }];


  const eventTypes = [
  "Aniversários", "Churrascos", "Confraternizações", "Eventos corporativos",
  "Casamentos", "Festas em casa", "Condomínios", "Eventos pequenos ou grandes"];


  return (
    <section id="eventos" className="section-padding bg-[#F5F5F7]" data-testid="ideal-para-section">
      <div className="container-main">
        <motion.div
          className="text-center mb-12"
          {...fadeInUp}>

          <h2 className="text-heading-2 text-[#1D1D1F] mb-4">
            Perfeito para qualquer tipo de evento
          </h2>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-3 gap-6 mb-12"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}>

          {events.map((event, index) =>
          <motion.div
            key={index}
            className="event-card"
            variants={fadeInUp}
            data-testid={`event-card-${index}`}>

              <img src={event.image} alt={event.title} />
              <div className="event-card-overlay">
                <h3 className="text-xl font-semibold">{event.title}</h3>
              </div>
            </motion.div>
          )}
        </motion.div>

        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-8"
          {...fadeInUp}>

          {eventTypes.map((type, index) =>
          <span
            key={index}
            className="bg-white px-4 py-2 rounded-full text-sm text-[#1D1D1F] border border-[#E5E5EA]">

              {type}
            </span>
          )}
        </motion.div>

        <motion.div
          className="text-center"
          {...fadeInUp}>

          <p className="text-xl text-[#1D1D1F] font-medium mb-6">Tem galera? Tem que ter chopp.</p>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="ideal-para-cta-btn">

            Solicitar orçamento
            <ChevronRight className="w-5 h-5" />
          </a>
        </motion.div>
      </div>
    </section>);

};

// Chopeira Section
const ChopeiraSection = () => {
  return (
    <section className="section-padding bg-white" data-testid="chopeira-section">
      <div className="container-main">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}>

            <h2 className="text-heading-2 text-[#1D1D1F] mb-6">
              Chopeira profissional no seu evento
            </h2>
            <p className="text-body text-[#86868B] mb-8">
              Nossas chopeiras elétricas garantem chopp gelado do primeiro ao último copo.
              Instalação rápida, operação simples e visual que valoriza sua festa.
            </p>
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#005BEA]/10 flex items-center justify-center">
                  <Thermometer className="w-5 h-5 text-[#005BEA]" />
                </div>
                <span className="text-[#1D1D1F] font-medium">Sempre Gelado</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#005BEA]/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#005BEA]" />
                </div>
                <span className="text-[#1D1D1F] font-medium">Instalação Rápida</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#005BEA]/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#005BEA]" />
                </div>
                <span className="text-[#1D1D1F] font-medium">Torneira Dupla</span>
              </div>
            </div>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="chopeira-cta-btn">

              Quero saber mais
              <ChevronRight className="w-5 h-5" />
            </a>
          </motion.div>

          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}>

            <div className="bg-[#F5F5F7] rounded-3xl p-8 lg:p-12">
              <img
                src={ASSETS.chopeira}
                alt="Chopeira Multi Chopp Express"
                className="w-full max-w-sm mx-auto drop-shadow-xl" />

            </div>
          </motion.div>
        </div>
      </div>
    </section>);

};

// Depoimentos Section
const DepoimentosSection = () => {
  const testimonials = [
  {
    text: "Atendimento rápido, chopp gelado e instalação perfeita. Todo mundo elogiou.",
    author: "Cliente Multi Chopp Express"
  },
  {
    text: "Facilitou demais nossa confraternização. Chegou no horário e funcionou perfeitamente.",
    author: "Cliente Multi Chopp Express"
  },
  {
    text: "Ótimo custo-benefício e chopp muito bom. Com certeza vamos pedir novamente.",
    author: "Cliente Multi Chopp Express"
  }];


  return (
    <section className="section-padding bg-[#F5F5F7]" data-testid="depoimentos-section">
      <div className="container-main">
        <motion.div
          className="text-center mb-12"
          {...fadeInUp}>

          <h2 className="text-heading-2 text-[#1D1D1F] mb-4">
            Quem pede, recomenda
          </h2>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-3 gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}>

          {testimonials.map((testimonial, index) =>
          <motion.div
            key={index}
            className="testimonial-card"
            variants={fadeInUp}
            data-testid={`testimonial-${index}`}>

              <div className="flex gap-1 mb-4 star-rating">
                {[...Array(5)].map((_, i) =>
              <Star key={i} className="w-5 h-5 fill-current" />
              )}
              </div>
              <p className="text-[#1D1D1F] mb-4 text-lg">"{testimonial.text}"</p>
              <p className="text-[#86868B] text-sm">— {testimonial.author}</p>
            </motion.div>
          )}
        </motion.div>

        <motion.div
          className="text-center mt-12"
          {...fadeInUp}>

          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp inline-flex"
            data-testid="depoimentos-cta-btn">

            <MessageCircle className="w-5 h-5" />
            Pedir meu chopp
          </a>
        </motion.div>
      </div>
    </section>);

};

// Formulário de Orçamento Section
const FormularioOrcamentoSection = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    data_evento: '',
    tipo_evento: '',
    quantidade_pessoas: '',
    marcas_interesse: [],
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const gerarCaptcha = () => ({
    a: Math.floor(Math.random() * 9) + 1,
    b: Math.floor(Math.random() * 9) + 1,
  });
  const [captcha, setCaptcha] = useState(gerarCaptcha);
  const [captchaInput, setCaptchaInput] = useState('');

  const tiposEvento = [
  'Aniversário',
  'Casamento',
  'Churrasco',
  'Confraternização',
  'Evento Corporativo',
  'Festa em Casa',
  'Outro'];


  const marcasDisponiveis = [
  'Harden Chopp Pilsen',
  'Brahma Chopp',
  'Heineken Chopp',
  'Chopp de Vinho'];


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMarcaToggle = (marca) => {
    setFormData((prev) => ({
      ...prev,
      marcas_interesse: prev.marcas_interesse.includes(marca) ?
      prev.marcas_interesse.filter((m) => m !== marca) :
      [...prev.marcas_interesse, marca]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validação básica
    if (!formData.nome || !formData.telefone || !formData.tipo_evento) {
      setError('Por favor, preencha os campos obrigatórios.');
      setIsSubmitting(false);
      return;
    }

    // Validação do captcha
    if (parseInt(captchaInput, 10) !== captcha.a + captcha.b) {
      setError('Resposta do captcha incorreta. Tente novamente.');
      setCaptcha(gerarCaptcha());
      setCaptchaInput('');
      setIsSubmitting(false);
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '/backend/orcamento.php';
      const endpointUrl = backendUrl.endsWith('.php')
        ? backendUrl
        : `${backendUrl}/backend/orcamento.php`;
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSubmitted(true);
        setCaptcha(gerarCaptcha());
        setCaptchaInput('');
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          data_evento: '',
          tipo_evento: '',
          quantidade_pessoas: '',
          marcas_interesse: [],
          mensagem: ''
        });
      } else {
        throw new Error('Erro ao enviar');
      }
    } catch (err) {
      // Fallback: abre WhatsApp com os dados preenchidos
      const mensagemWhatsApp = `Olá! Gostaria de solicitar um orçamento:

*Nome:* ${formData.nome}
*Telefone:* ${formData.telefone}
*Email:* ${formData.email || 'Não informado'}
*Data do Evento:* ${formData.data_evento || 'A definir'}
*Tipo de Evento:* ${formData.tipo_evento}
*Quantidade de Pessoas:* ${formData.quantidade_pessoas || 'A definir'}
*Marcas de Interesse:* ${formData.marcas_interesse.length > 0 ? formData.marcas_interesse.join(', ') : 'A definir'}
*Observações:* ${formData.mensagem || 'Nenhuma'}`;

      const whatsappUrl = `https://wa.me/5541997019715?text=${encodeURIComponent(mensagemWhatsApp)}`;
      window.open(whatsappUrl, '_blank');
      setIsSubmitted(true);
    }

    setIsSubmitting(false);
  };

  if (isSubmitted) {
    return (
      <section id="orcamento" className="section-padding bg-white" data-testid="orcamento-section">
        <div className="container-main">
          <motion.div
            className="max-w-2xl mx-auto text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}>

            <div className="bg-[#F5F5F7] rounded-3xl p-12">
              <div className="w-20 h-20 bg-[#25D366] rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-heading-3 text-[#1D1D1F] mb-4">
                Solicitação Enviada!
              </h3>
              <p className="text-[#86868B] mb-8">
                Recebemos seu pedido de orçamento. Nossa equipe entrará em contato em breve pelo telefone ou WhatsApp informado.
              </p>
              <button
                onClick={() => setIsSubmitted(false)}
                className="btn-primary"
                data-testid="novo-orcamento-btn">

                Fazer Novo Orçamento
              </button>
            </div>
          </motion.div>
        </div>
      </section>);

  }

  return (
    <section id="orcamento" className="section-padding bg-white" data-testid="orcamento-section">
      <div className="container-main">
        <motion.div
          className="text-center mb-12"
          {...fadeInUp}>

          <h2 className="text-heading-2 text-[#1D1D1F] mb-4">
            Solicite seu Orçamento
          </h2>
          <p className="text-body text-[#86868B] max-w-2xl mx-auto">
            Preencha o formulário abaixo e nossa equipe entrará em contato para montar o pacote perfeito para seu evento.
          </p>
        </motion.div>

        <motion.div
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}>

          <form onSubmit={handleSubmit} className="card-apple p-8 md:p-12">
            {error &&
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
                {error}
              </div>
            }

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  Nome Completo *
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#86868B]" />
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    placeholder="Seu nome"
                    className="w-full pl-12 pr-4 py-3 border border-[#E5E5EA] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#005BEA] focus:border-transparent transition-all"
                    required
                    data-testid="input-nome" />

                </div>
              </div>

              {/* Telefone */}
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  Telefone/WhatsApp *
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#86868B]" />
                  <input
                    type="tel"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleChange}
                    placeholder="(41) 99999-9999"
                    className="w-full pl-12 pr-4 py-3 border border-[#E5E5EA] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#005BEA] focus:border-transparent transition-all"
                    required
                    data-testid="input-telefone" />

                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  E-mail
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#86868B]" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="seu@email.com"
                    className="w-full pl-12 pr-4 py-3 border border-[#E5E5EA] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#005BEA] focus:border-transparent transition-all"
                    data-testid="input-email" />

                </div>
              </div>

              {/* Data do Evento */}
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  Data do Evento
                </label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#86868B]" />
                  <input
                    type="date"
                    name="data_evento"
                    value={formData.data_evento}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3 border border-[#E5E5EA] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#005BEA] focus:border-transparent transition-all"
                    data-testid="input-data" />

                </div>
              </div>

              {/* Tipo de Evento */}
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  Tipo de Evento *
                </label>
                <select
                  name="tipo_evento"
                  value={formData.tipo_evento}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-[#E5E5EA] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#005BEA] focus:border-transparent transition-all appearance-none bg-white"
                  required
                  data-testid="select-tipo-evento">

                  <option value="">Selecione o tipo</option>
                  {tiposEvento.map((tipo) =>
                  <option key={tipo} value={tipo}>{tipo}</option>
                  )}
                </select>
              </div>

              {/* Quantidade de Pessoas */}
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  Quantidade de Pessoas
                </label>
                <div className="relative">
                  <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#86868B]" />
                  <input
                    type="number"
                    name="quantidade_pessoas"
                    value={formData.quantidade_pessoas}
                    onChange={handleChange}
                    placeholder="Ex: 50"
                    min="1"
                    className="w-full pl-12 pr-4 py-3 border border-[#E5E5EA] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#005BEA] focus:border-transparent transition-all"
                    data-testid="input-quantidade" />

                </div>
              </div>
            </div>

            {/* Marcas de Interesse */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1D1D1F] mb-3">
                Marcas de Interesse
              </label>
              <div className="flex flex-wrap gap-3">
                {marcasDisponiveis.map((marca) =>
                <button
                  key={marca}
                  type="button"
                  onClick={() => handleMarcaToggle(marca)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  formData.marcas_interesse.includes(marca) ?
                  'bg-[#005BEA] text-white' :
                  'bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E5E5EA]'}`
                  }
                  data-testid={`marca-${marca.toLowerCase().replace(/\s/g, '-')}`}>

                    {marca}
                  </button>
                )}
              </div>
            </div>

            {/* Mensagem */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                Observações ou Dúvidas
              </label>
              <div className="relative">
                <FileText className="absolute left-4 top-4 w-5 h-5 text-[#86868B]" />
                <textarea
                  name="mensagem"
                  value={formData.mensagem}
                  onChange={handleChange}
                  placeholder="Conte mais sobre seu evento ou tire suas dúvidas..."
                  rows={4}
                  className="w-full pl-12 pr-4 py-3 border border-[#E5E5EA] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#005BEA] focus:border-transparent transition-all resize-none"
                  data-testid="textarea-mensagem" />

              </div>
            </div>

            {/* Captcha */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                Verificação: quanto é <strong>{captcha.a} + {captcha.b}</strong>?
              </label>
              <input
                type="number"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                placeholder="Digite o resultado"
                required
                className="w-full px-4 py-3 border border-[#E5E5EA] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#005BEA] focus:border-transparent transition-all"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              data-testid="submit-orcamento-btn">

              {isSubmitting ?
              <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Enviando...
                </> :

              <>
                  <Send className="w-5 h-5" />
                  Solicitar Orçamento
                </>
              }
            </button>

            <p className="text-center text-sm text-[#86868B] mt-4">
              Ou se preferir, entre em contato direto pelo{' '}
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#25D366] font-medium hover:underline">

                WhatsApp
              </a>
            </p>
          </form>
        </motion.div>
      </div>
    </section>);

};

// CTA Final Section
const CTAFinalSection = () => {
  return (
    <section className="cta-section section-padding" data-testid="cta-final-section">
      <div className="container-main text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}>

          <h2 className="text-heading-2 text-white mb-4">
            Seu evento merece um chopp de verdade
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Fale com a Multi Chopp Express e garanta agora
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#005BEA] hover:bg-white/90 px-8 py-4 rounded-full font-medium transition-all duration-300 inline-flex items-center justify-center gap-2"
              data-testid="cta-final-whatsapp-btn">

              <MessageCircle className="w-5 h-5" />
              Pedir pelo WhatsApp
            </a>
            <button
              onClick={() => document.getElementById('marcas').scrollIntoView({ behavior: 'smooth' })}
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-medium transition-all duration-300"
              data-testid="cta-final-marcas-btn">

              Ver disponibilidade
            </button>
          </div>
        </motion.div>
      </div>
    </section>);

};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-[#1D1D1F] text-white py-16" data-testid="footer">
      <div className="container-main">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          <div>
            <img src={ASSETS.logo} alt="Multi Chopp Express" className="h-12 mb-4" />
            <p className="text-zinc-400">
              Entrega de chopp com chopeira instalada
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Atendimento</h4>
            <p className="text-zinc-400 flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4" />
              Curitiba e região metropolitana
            </p>
            <p className="text-zinc-400 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              (41) 99701-9715
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Redes Sociais</h4>
            <a
              href={INSTAGRAM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2"
              data-testid="footer-instagram-link">

              <Instagram className="w-5 h-5" />
              @multichoppexpress
            </a>
          </div>
        </div>
        <div className="border-t border-zinc-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-500 text-sm">
            © {new Date().getFullYear()} Multi Chopp Express. Todos os direitos reservados.
          </p>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp text-sm px-6 py-3"
            data-testid="footer-whatsapp-btn">

            <MessageCircle className="w-4 h-4" />
            Chamar no WhatsApp
          </a>
        </div>
      </div>
    </footer>);

};

// Floating WhatsApp Button
const FloatingWhatsApp = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {isVisible &&
      <motion.a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="floating-whatsapp"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        whileHover={{ scale: 1.1 }}
        data-testid="floating-whatsapp-btn">

          <MessageCircle className="w-6 h-6" />
        </motion.a>
      }
    </AnimatePresence>);

};

// Main Landing Page
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <HeroSection />
        <DiferencialSection />
        <MarcasSection />
        <ComoFuncionaSection />
        <IdealParaSection />
        <ChopeiraSection />
        <DepoimentosSection />
        <FormularioOrcamentoSection />
        <CTAFinalSection />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>);

};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
    </div>);

}

export default App;